/**
 * One language at a time (T13e-2): the reader picks a door once, in the top bar, and every code block on
 * every page shows it. Each block carries all five, and the stylesheet shows the one `html[data-lang]` names,
 * so the pages stay static and never flash.
 */
import { DOOR_LABEL, SHIPPED_DOORS, type Door } from "../_content/card";
import type { IconName } from "../_family/Icon";

/** The door a page shows before the reader picks one, with no script, or after a choice it does not know. */
export const DEFAULT_LANG: Door = "ts";

/** Where the choice is kept, in the reader's own browser. */
export const LANG_KEY = "vestra-docs-lang";

/** Each door as the switch names it: in full, short on a phone, in a few words, and its icon. */
export const LANGS: ReadonlyArray<{
  door: Door;
  label: string;
  short: string;
  says: string;
  icon: IconName;
}> = SHIPPED_DOORS.map((door) => ({
  door,
  label: DOOR_LABEL[door],
  ...(
    {
      ts: { short: "TS", says: "The TypeScript SDK", icon: "fileTs" },
      py: { short: "Py", says: "The Python SDK", icon: "filePy" },
      cli: {
        short: "CLI",
        says: "The vestra command, in your terminal",
        icon: "terminal",
      },
      http: { short: "HTTP", says: "Plain curl, or any client", icon: "globe" },
      mcp: { short: "MCP", says: "The tools an agent calls", icon: "mcp" },
    } as const
  )[door],
}));

export function isLang(value: string | null | undefined): value is Door {
  return (SHIPPED_DOORS as readonly string[]).includes(value ?? "");
}

/**
 * Run before the page paints (inline, in the docs layout): the kept choice, when it is one of the doors, goes
 * on `<html data-lang>`; anything else leaves the default. `data-docs` says scripts run, so the sticky code
 * panel may follow the step in view (without it, each step keeps its code under its words).
 */
export const LANG_BOOTSTRAP = `(function(){var d=document.documentElement;d.setAttribute("data-docs","js");try{var l=localStorage.getItem(${JSON.stringify(LANG_KEY)});if(/^(${SHIPPED_DOORS.join("|")})$/.test(l||""))d.setAttribute("data-lang",l)}catch(e){}})();`;
